import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useKey, usePrevious } from 'react-use';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import useBodyLock from '@sorare/core/src/hooks/useBodyLock';

import useMarkStoriesAsRead from 'hooks/stories/useMarkStoriesAsRead';

import { CardLeveledUp } from '../CardLeveledUp';
import { CardRewardsReveal } from '../CardRewardsReveal';
import { Intro } from '../Intro';
import { Promotion } from '../Promotion';
import { Relegation } from '../Relegation';
import { RewardsRecap } from '../RewardsRecap';
import { useRewardsVerification } from '../RewardsRecap/useRewardsVerification';
import { StoryState } from '../types';
import type {
  RewardsFlow_cardXpGradeThreshold,
  RewardsFlow_leveledUpCard,
} from './__generated__/index.graphql';
import {
  RewardsFlow_so5Fixture,
  RewardsFlow_so5Reward,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: var(--c-black);
`;

type RewardsFlowProps = {
  storyId?: string;
  so5Fixture?: RewardsFlow_so5Fixture;
  so5Rewards: RewardsFlow_so5Reward[];
  leveledUpCards?: RewardsFlow_leveledUpCard[] | null;
  gradeThresholds?: RewardsFlow_cardXpGradeThreshold[];
  initialStep?: StoryState;
  onClose?: () => void;
};
export const RewardsFlow = ({
  storyId,
  so5Fixture,
  so5Rewards: so5RewardsProp,
  leveledUpCards,
  gradeThresholds,
  initialStep,
  onClose,
}: RewardsFlowProps) => {
  const [so5Rewards, setSo5Rewards] = useState<RewardsFlow_so5Reward[]>();
  if (!so5Rewards && so5RewardsProp?.length > 0) {
    setSo5Rewards(so5RewardsProp);
  }

  const [open, setOpen] = useState(true);
  const [storyState, setStoryState] = useState<StoryState>(
    initialStep || 'intro'
  );
  const prevStoryState = usePrevious(storyState);
  const { readStories: readFixtureStories, loading } = useMarkStoriesAsRead();
  const { unclaimedRewardsByType } = useRewardsVerification(so5Rewards || []);

  const finalOnClose = async () => {
    setOpen(false);
    onClose?.();
    if (storyId && !loading) {
      await readFixtureStories({
        storyIds: [storyId],
      });
    }
  };
  useKey('Escape', finalOnClose);
  useBodyLock(open);

  const hasUnclaimedReward = unclaimedRewardsByType.total > 0;
  const hasCardRewards = unclaimedRewardsByType.card.length > 0;

  const hasPromotion = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.PROMOTED
  );
  const hasRelegation = so5Fixture?.mySo5Rankings?.some(
    ranking => ranking.eligibleDivisionChange === DivisionChange.RELEGATED
  );
  const hasCardLeveledUps = (leveledUpCards?.length || 0) > 0;

  return (
    <Modal>
      <Wrapper>
        {storyState === 'intro' && (
          <Intro
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onNext={
              hasPromotion ||
              hasRelegation ||
              hasCardLeveledUps ||
              hasUnclaimedReward
                ? () => {
                    if (hasPromotion) {
                      setStoryState('promotion');
                    } else if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'promotion' && (
          <Promotion
            so5Fixture={so5Fixture}
            onNext={
              hasRelegation || hasCardLeveledUps || hasUnclaimedReward
                ? () => {
                    if (hasRelegation) {
                      setStoryState('relegation');
                    } else if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'relegation' && (
          <Relegation
            from={prevStoryState}
            so5Fixture={so5Fixture}
            onNext={
              hasCardLeveledUps || hasUnclaimedReward
                ? () => {
                    if (hasCardLeveledUps) {
                      setStoryState('cardLeveledUp');
                    } else if (hasUnclaimedReward) {
                      setStoryState('rewardsRecap');
                    }
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardLeveledUp' && (
          <CardLeveledUp
            from={prevStoryState}
            so5Fixture={so5Fixture}
            leveledUpCards={leveledUpCards || []}
            gradeThresholds={gradeThresholds}
            onNext={
              hasUnclaimedReward
                ? () => {
                    setStoryState('rewardsRecap');
                  }
                : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'rewardsRecap' && (
          <RewardsRecap
            from={prevStoryState}
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onNext={
              hasCardRewards ? () => setStoryState('cardReveal') : undefined
            }
            onClose={finalOnClose}
          />
        )}
        {storyState === 'cardReveal' && (
          <CardRewardsReveal
            so5Fixture={so5Fixture}
            so5Rewards={so5Rewards || []}
            onClose={finalOnClose}
          />
        )}
      </Wrapper>
    </Modal>
  );
};
RewardsFlow.fragments = {
  cardXpGradeThreshold: gql`
    fragment RewardsFlow_cardXpGradeThreshold on CardXPGradeThreshold {
      ...CardLeveledUp_cardXpGradeThreshold
    }
    ${CardLeveledUp.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<RewardsFlow_cardXpGradeThreshold>,
  leveledUpCard: gql`
    fragment RewardsFlow_leveledUpCard on LeveledUpCard {
      id
      ...CardLeveledup_leveledUpCard
    }
    ${CardLeveledUp.fragments.leveledUpCard}
  ` as TypedDocumentNode<RewardsFlow_leveledUpCard>,
  so5Reward: gql`
    fragment RewardsFlow_so5Reward on So5Reward {
      slug
      aasmState
      ...Intro_so5Reward
      ...RewardsRecap_so5Reward
      ...CardRewardsReveal_so5Reward
      ...useRewardsVerification_so5Reward
    }
    ${Intro.fragments.so5Reward}
    ${RewardsRecap.fragments.so5Reward}
    ${CardRewardsReveal.fragments.so5Reward}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsFlow_so5Reward>,
  so5Fixture: gql`
    fragment RewardsFlow_so5Fixture on So5Fixture {
      slug
      sport
      mySo5Rankings {
        id
        eligibleDivisionChange
      }
      ...Intro_so5Fixture
      ...Promotion_so5Fixture
      ...Relegation_so5Fixture
      ...RewardsRecap_so5Fixture
      ...CardRewardsReveal_so5Fixture
      ...CardLeveledUp_so5Fixture
    }
    ${Intro.fragments.so5Fixture}
    ${Promotion.fragments.so5Fixture}
    ${Relegation.fragments.so5Fixture}
    ${RewardsRecap.fragments.so5Fixture}
    ${CardRewardsReveal.fragments.so5Fixture}
    ${CardLeveledUp.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsFlow_so5Fixture>,
};
